<script setup lang="ts">
import type { SelectContentEmits, SelectContentProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { reactiveOmit } from '@vueuse/core'
import { SelectContent, SelectPortal, SelectViewport, useForwardPropsEmits } from 'reka-ui'
import { ref } from 'vue'
import { useFloatingZ } from '#ui-kit/lib/useFloatingZ'
import { cn } from '#ui-kit/lib/utils'
import SelectScrollDownButton from './SelectScrollDownButton.vue'
import SelectScrollUpButton from './SelectScrollUpButton.vue'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<SelectContentProps & { class?: HTMLAttributes['class'] }>(), {
  position: 'popper',
  bodyLock: false,
})
const emits = defineEmits<SelectContentEmits>()

const delegatedProps = reactiveOmit(props, 'class')

const forwarded = useForwardPropsEmits(delegatedProps, emits)

const hasInteractedOutsideRef = ref(false)
const floatingZ = useFloatingZ()

function handlePointerDownOutside() {
  hasInteractedOutsideRef.value = true
}

function handleCloseAutoFocus(event: Event) {
  if (hasInteractedOutsideRef.value) {
    event.preventDefault()
    hasInteractedOutsideRef.value = false
  }
}
</script>

<template>
  <SelectPortal>
    <SelectContent
      :disable-outside-pointer-events="false"
      v-bind="{ ...forwarded, ...$attrs }"
      :class="
        cn(
          'relative max-h-96 overflow-hidden rounded-lg bg-white text-primal border border-gray-200 shadow-lg outline-hidden font-sans font-normal data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          position === 'popper'
            ? 'min-w-(--reka-select-trigger-width) data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1'
            : 'min-w-32',
          props.class,
        )
      "
      :style="{ zIndex: floatingZ }"
      @pointer-down-outside="handlePointerDownOutside"
      @close-auto-focus="handleCloseAutoFocus"
    >
      <SelectScrollUpButton />
      <SelectViewport
        :class="cn('p-2 scroll-py-8', position === 'popper' && 'h-(--reka-select-trigger-height) w-full')"
      >
        <slot />
      </SelectViewport>
      <SelectScrollDownButton />
    </SelectContent>
  </SelectPortal>
</template>
